import { assetDataUri, assetReferenceOf, type PreviewAssets } from '../preview/previewAssets';
import type { ListData } from './listDataFile';

/**
 * Replacing a picture (Prototype Editor UX plan, step 6).
 *
 * A prototype's pictures live in the workbench store as `public/media/<file>` and are referenced
 * from code as `/media/<file>`; the light preview swaps the reference for the bytes when it
 * compiles. So "replace this picture" is two things: put the new bytes where the old ones are
 * kept, and write the new reference where the old one was written - a `src` in the JSX, or a
 * field of the record when the picture comes from a list's data.
 *
 * Pure: the store's maps go in, paths and strings come out.
 */
export type MediaItem = { storePath: string; reference: string; name: string; dataUri: string };

const MEDIA_DIR = 'public/media/';
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|avif)$/i;

/** Every picture the prototype already has, newest last, for the library. */
export function mediaLibrary(assets: PreviewAssets): MediaItem[] {
  return Object.entries(assets)
    .filter(([path, asset]) => path.startsWith(MEDIA_DIR) && IMAGE_EXT.test(path) && asset?.base64)
    .map(([storePath, asset]) => ({
      storePath,
      reference: `/${assetReferenceOf(storePath)}`,
      name: storePath.slice(MEDIA_DIR.length),
      dataUri: assetDataUri(storePath, asset),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The store path of the asset the preview is showing. The preview's `<img>` carries the bytes,
 * not the name, so the name is found by matching the bytes back to the store.
 */
export function assetPathForDataUri(assets: PreviewAssets, src: string | undefined | null): string | null {
  if (!src || !src.startsWith('data:')) return null;
  for (const [storePath, asset] of Object.entries(assets)) {
    if (asset?.base64 && assetDataUri(storePath, asset) === src) return storePath;
  }
  return null;
}

/**
 * Where an uploaded file goes: under `public/media`, with a name safe for a URL and a suffix when
 * that name is already taken by different bytes.
 */
export function storePathForUpload(fileName: string, existing: PreviewAssets, base64: string): string {
  const dot = fileName.lastIndexOf('.');
  const ext = (dot > 0 ? fileName.slice(dot + 1) : 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
  const stem =
    (dot > 0 ? fileName.slice(0, dot) : fileName)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'picture';
  let candidate = `${MEDIA_DIR}${stem}.${ext}`;
  for (let n = 2; existing[candidate] && existing[candidate]!.base64 !== base64; n++) {
    candidate = `${MEDIA_DIR}${stem}-${n}.${ext}`;
  }
  return candidate;
}

/** `data:image/png;base64,....` split into what the store keeps. */
export function parseDataUri(dataUri: string): { base64: string; mimeType: string } | null {
  const m = /^data:([^;,]+);base64,(.+)$/s.exec(dataUri);
  return m ? { mimeType: m[1]!, base64: m[2]! } : null;
}

/** Whether a record field points at this asset, however the reference was written. */
export function fieldRefersTo(value: unknown, storePath: string): boolean {
  if (typeof value !== 'string') return false;
  const reference = assetReferenceOf(storePath);
  return value.replace(/^\.?\/+/, '') === reference;
}

/**
 * The record field holding the picture in a list's data, when the picture comes from data. The
 * row is the one the founder clicked; the field is whichever one names the asset on show.
 */
export function imageFieldOf(list: ListData, recordId: string, storePath: string | null): string | null {
  if (!storePath) return null;
  const record = list.records.find((r) => r.id === recordId);
  if (!record) return null;
  const field = record.fields.find((f) => f.kind === 'string' && fieldRefersTo(f.value, storePath));
  return field?.name ?? null;
}
